import React, { useRef, useState } from "react";
import { gsap, ScrollTrigger, useGSAP, MOTION_OK } from "../../lib/gsap";
import { scrollToY } from "../../lib/scroll";
import { projectData } from "../../data/projects";
import { workfloxProjects } from "../../data/workflox";
import { ArrowIcon, Mask, WorkfloxLink } from "./Chrome";

const nameKey = (name) => name.toLowerCase().replace(/[^a-z0-9]/g, "");

// The featured cards come from the Workflox portfolio; everything else from my
// own catalogue is listed underneath, minus the ones already featured.
const featured = new Set(workfloxProjects.map((p) => nameKey(p.name)));
const more = projectData.filter((p) => !featured.has(nameKey(p.name)));

const linkLabel = (url) =>
  url.includes("apps.apple.com") ? "App Store" : url.includes("figma.com") ? "Figma" : "Live site";

const STEP = 360; // px of scrolling per project while the carousel is pinned
const pad = (n) => String(n).padStart(2, "0");
const smooth = (v) => {
  const t = Math.min(1, Math.max(0, v));
  return t * t * (3 - 2 * t);
};

const Chevron = ({ flip }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d={flip ? "M15 5l-7 7 7 7" : "M9 5l7 7-7 7"} />
  </svg>
);

const External = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer">
    {children}
  </a>
);

function Work() {
  const root = useRef(null);
  const bar = useRef(null);
  const goTo = useRef(() => {});
  const [active, setActive] = useState(0);
  const total = workfloxProjects.length;

  /* Desktop: a 3D carousel driven by scrolling. The section pins; the project in
     front faces the visitor while its neighbours angle away into depth on either
     side, and each stretch of scroll turns the carousel to the next project.
     Small screens and reduced-motion visitors get a plain swipeable row. */
  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(`(min-width: 1025px) and ${MOTION_OK}`, () => {
        const section = root.current;
        const cards = gsap.utils.toArray(".project", section);
        const last = cards.length - 1;
        const state = { index: 0 };
        section.classList.add("work--flow");

        const render = () => {
          const width = cards[0].offsetWidth;
          cards.forEach((card, i) => {
            const offset = i - state.index;
            const distance = Math.abs(offset);
            const near = Math.min(distance, 1); // 0 in front … 1 once it is a neighbour
            const far = Math.max(distance - 1, 0); // how many places beyond that
            const side = Math.sign(offset);
            gsap.set(card, {
              x: side * (near * width * 0.8 + far * width * 0.32),
              z: -(near * 260 + far * 80),
              rotationY: -side * near * 40,
              autoAlpha: gsap.utils.clamp(0, 1, 1 - (distance - 2.4) / 1.2),
              zIndex: 100 - Math.round(distance * 10),
              "--shade": near * 0.62,
            });
          });
          bar.current.style.transform = `scaleX(${state.index / last})`;
          setActive(Math.round(state.index));
        };

        const trigger = ScrollTrigger.create({
          trigger: ".work-pin",
          start: "top top",
          end: () => `+=${last * STEP}`,
          pin: true,
          invalidateOnRefresh: true,
          // The fixed social rail would sit on top of the carousel
          onToggle: (self) => document.body.classList.toggle("rail-hidden", self.isActive),
          onUpdate: (self) => {
            // Rest on each project for a while, then turn briskly to the next
            const raw = self.progress * last;
            const whole = Math.floor(raw);
            const index = Math.min(last, whole + smooth((raw - whole - 0.2) / 0.6));
            gsap.to(state, { index, duration: 0.5, ease: "power3.out", overwrite: true, onUpdate: render });
          },
          onRefresh: render,
        });
        render();

        goTo.current = (i) => {
          const index = gsap.utils.clamp(0, last, i);
          scrollToY(trigger.start + (index / last) * (trigger.end - trigger.start));
        };

        return () => {
          goTo.current = () => {};
          section.classList.remove("work--flow");
          document.body.classList.remove("rail-hidden");
          gsap.killTweensOf(state);
          gsap.set(cards, { clearProps: "all" });
        };
      });
      return () => mm.revert();
    },
    { scope: root }
  );

  return (
    <section id="work" className="section work" ref={root}>
      <div className="work-pin">
        <div className="wrap work-head">
          <div>
            <p className="eyebrow">Selected work</p>
            <h2 className="section-title">
              <Mask>
                My <em>work</em>
              </Mask>
            </h2>
          </div>
          <p data-reveal>
            Products I&rsquo;ve led at Workflox — mobile apps, web platforms and AI systems, each shipped to real
            users. Case studies open on workflox.net.
          </p>
        </div>

        <div className="work-track">
          {workfloxProjects.map((p, i) => (
            <article
              key={`${p.name}-${p.kind}`}
              className={`project ${i === active ? "is-active" : ""}`}
              onClick={() => i !== active && goTo.current(i)}
            >
              <div className="project-art">
                {p.image && <img src={p.image} alt={`${p.name} — ${p.kind}`} loading="lazy" />}
              </div>
              <div className="project-body">
                <div className="project-meta">
                  <h3>{p.name}</h3>
                  <small>{pad(i + 1)}</small>
                </div>
                <p className="project-cat">{p.kind}</p>
                <p className="project-summary">{p.summary}</p>
                <div className="tags">
                  {p.stack.slice(0, 3).map((t) => (
                    <span key={t} className="tag">
                      {t}
                    </span>
                  ))}
                </div>
                <div className="project-links">
                  {p.caseStudy && (
                    <WorkfloxLink href={p.caseStudy}>
                      Case study <ArrowIcon />
                    </WorkfloxLink>
                  )}
                  {p.url && (
                    <External href={p.url}>
                      {linkLabel(p.url)} <ArrowIcon />
                    </External>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Carousel controls; hidden when the section falls back to a swipe row */}
        <div className="wrap work-foot">
          <p className="work-count" aria-live="polite">
            <strong>{pad(active + 1)}</strong> / {pad(total)}
          </p>
          <div className="work-progress">
            <span ref={bar} />
          </div>
          <div className="work-arrows">
            <button type="button" aria-label="Previous project" disabled={active === 0} onClick={() => goTo.current(active - 1)}>
              <Chevron flip />
            </button>
            <button
              type="button"
              aria-label="Next project"
              disabled={active === total - 1}
              onClick={() => goTo.current(active + 1)}
            >
              <Chevron />
            </button>
          </div>
        </div>
      </div>

      <div className="wrap more-work">
        <h3>More shipped &amp; designed work</h3>
        <ul className="more-list">
          {more.map((p) => (
            <li key={p.name} className="more-row" data-reveal>
              <div>
                <strong>{p.name}</strong>
                <span>{p.category}</span>
              </div>
              <div className="project-links">
                {p.appStore && <External href={p.appStore}>App Store</External>}
                {p.playStore && <External href={p.playStore}>Google Play</External>}
                {p.figma && <External href={p.figma}>Figma</External>}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export default Work;
